/**
 * 命盘职引 — 隐私工具模块
 * 
 * 设计原则：
 * - 所有八字历史记录默认仅存储在用户设备（localStorage）
 * - 不会自动上传到服务器
 * - 用户可以随时删除本地数据
 * - 排盘计算在本地后端完成，不经过第三方
 */

const BAZI_HISTORY_KEY = 'mingpan_bazi_history';
const FEATURE_USAGE_KEY = 'mingpan_feature_usage';
const PRIVACY_ACCEPTED_KEY = 'mingpan_privacy_accepted';
const HISTORY_MAX = 20; // 最多保留20条记录

// ========== 隐私同意状态 ==========

export function hasAcceptedPrivacy() {
  return localStorage.getItem(PRIVACY_ACCEPTED_KEY) === 'true';
}

export function acceptPrivacy() {
  localStorage.setItem(PRIVACY_ACCEPTED_KEY, 'true');
}

// ========== 八字历史记录管理（本地存储） ==========

/**
 * 生成记录标签（不暴露完整出生日期）
 */
function generateLabel(baziData) {
  const dm = baziData.day_master || '未知';
  const pattern = baziData.pattern || '';
  const gender = baziData.gender === 'male' ? '男' : baziData.gender === 'female' ? '女' : '';
  const parts = [dm];
  if (pattern) parts.push(pattern);
  if (gender) parts.push(gender);
  return parts.join(' · ');
}

/**
 * 保存八字排盘记录到本地存储
 * 注意：不保存精确出生日期，只保存八字结果
 */
export function saveBaziRecord(baziData) {
  if (!baziData) return null;

  const existing = getBaziRecords();
  
  // 去重：相同日柱+时柱+性别视为同一条
  const isDuplicate = existing.some(r => {
    const sameDayGan = r.four_pillars?.day?.gan === baziData.four_pillars?.day?.gan;
    const sameDayZhi = r.four_pillars?.day?.zhi === baziData.four_pillars?.day?.zhi;
    const sameTimeZhi = r.four_pillars?.hour?.zhi === baziData.four_pillars?.hour?.zhi;
    const sameGender = r.gender === baziData.gender;
    return sameDayGan && sameDayZhi && sameTimeZhi && sameGender;
  });

  if (isDuplicate) return null;

  const record = {
    id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
    label: generateLabel(baziData),
    timestamp: new Date().toISOString(),
    // 八字结果数据（不包含精确出生日期）
    four_pillars: baziData.four_pillars,
    ten_gods: baziData.ten_gods,
    five_elements: baziData.five_elements,
    day_master: baziData.day_master,
    day_master_strength: baziData.day_master_strength,
    favorable_elements: baziData.favorable_elements,
    unfavorable_elements: baziData.unfavorable_elements,
    pattern: baziData.pattern,
    nayin: baziData.nayin,
    nayin_year: baziData.nayin_year,
    shensha: baziData.shensha,
    gender: baziData.gender,
    // 仅保存出生年份/月份/地点用于显示（不存具体日期）
    birth_year: baziData.year || baziData.birth_year,
    birth_month: baziData.month || baziData.birth_month,
    location: baziData.location,
    calendar_type: baziData.calendar_type,
  };

  const updated = [record, ...existing].slice(0, HISTORY_MAX);
  localStorage.setItem(BAZI_HISTORY_KEY, JSON.stringify(updated));
  return record;
}

/**
 * 获取所有八字历史记录
 */
export function getBaziRecords() {
  try {
    const raw = localStorage.getItem(BAZI_HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/**
 * 删除单条记录
 */
export function deleteBaziRecord(id) {
  const records = getBaziRecords().filter(r => r.id !== id);
  localStorage.setItem(BAZI_HISTORY_KEY, JSON.stringify(records));
  return records;
}

/**
 * 清空所有记录
 */
export function clearAllBaziRecords() {
  localStorage.removeItem(BAZI_HISTORY_KEY);
  localStorage.removeItem(FEATURE_USAGE_KEY);
}

/**
 * 检查是否有历史记录
 */
export function hasBaziHistory() {
  return getBaziRecords().length > 0;
}

// ========== 功能使用追踪 ==========

/**
 * 功能键定义及显示信息
 */
export const FEATURE_META = {
  'career': { label: '💼 职业建议', color: 'bg-zhusha/10 text-zhusha', path: '/career' },
  'fortune': { label: '🌅 每日运势', color: 'bg-gold/10 text-gold', path: '/fortune' },
  'fortune-cycles': { label: '🔮 大运流年', color: 'bg-purple-600/10 text-purple-600', path: '/fortune-cycles' },
  'love': { label: '💕 择偶匹配', color: 'bg-rose-500/10 text-rose-500', path: '/love' },
  'select-date': { label: '📅 择日决策', color: 'bg-ink/10 text-ink', path: '/select-date' },
  'life-guide': { label: '🧭 生活指南', color: 'bg-teal-600/10 text-teal-600', path: '/life-guide' },
  'energy-diary': { label: '📖 能量日记', color: 'bg-amber-500/10 text-amber-500', path: '/energy-diary' },
  'study': { label: '📚 学业建议', color: 'bg-blue-500/10 text-blue-600', path: '/study' },
  'invest': { label: '💰 投资建议', color: 'bg-emerald-500/10 text-emerald-600', path: '/invest' },
};

/**
 * 记录某个八字记录的功能使用
 */
export function recordFeatureUsage(recordId, featureKey) {
  if (!recordId || !featureKey) return;
  const all = _getFeatureUsageMap();
  if (!all[recordId]) {
    all[recordId] = [];
  }
  if (!all[recordId].includes(featureKey)) {
    all[recordId].push(featureKey);
  }
  localStorage.setItem(FEATURE_USAGE_KEY, JSON.stringify(all));
}

/**
 * 获取某个八字记录已使用过的功能列表
 */
export function getFeatureUsage(recordId) {
  const all = _getFeatureUsageMap();
  return all[recordId] || [];
}

function _getFeatureUsageMap() {
  try {
    const raw = localStorage.getItem(FEATURE_USAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

// ========== 隐私标注组件数据 ==========

/**
 * 返回不同场景下的隐私说明
 */
export function getPrivacyInfo(type) {
  const map = {
    'bazi_calc': {
      icon: '🔒',
      label: '本地计算',
      desc: '八字排盘在您的设备本地完成，出生信息不会上传到云端',
      level: 'local',
    },
    'bazi_storage': {
      icon: '📱',
      label: '仅存储在您的设备',
      desc: '排盘记录仅保存在浏览器本地，您可以随时删除',
      level: 'local',
    },
    'ai_feature': {
      icon: '🌐',
      label: '需要联网AI服务',
      desc: '使用AI深度分析时，八字命盘数据会发送至第三方AI服务商（DeepSeek/OpenAI）进行处理',
      level: 'external',
    },
    'local_analysis': {
      icon: '🔒',
      label: '本地分析',
      desc: '此分析完全在本地完成，不涉及第三方服务',
      level: 'local',
    },
    'partner_match': {
      icon: '🔒',
      label: '合盘本地计算',
      desc: '合盘匹配度在本地后端计算，对方信息不会被保存',
      level: 'local',
    },
  };
  return map[type] || map['local_analysis'];
}
